// Hand-rolled SVG chart primitives for the analytics tab.
//
// Deliberately dependency-free: the site ships no charting library, and every
// metric here is a trend over time, which is one shape — a line, with an area
// wash under a lone series. The rules:
//   * one y-axis, always zero-based — never two scales on one plot
//   * 2px lines, hairline solid gridlines, recessive axis text
//   * a legend whenever there are ≥2 series, plus a table view on every card,
//     so identity and values never depend on colour alone
//   * a crosshair tooltip that lists every series at the hovered bucket,
//     reachable by keyboard (focus the plot, then ← / →)
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Table2, LineChart as LineChartIcon } from 'lucide-react';
import { INK } from './palette';
import {
	Granularity,
	Point,
	Unit,
	formatBucket,
	formatBucketLong,
	formatByUnit,
	formatCompact,
} from '../../utils/analyticsApi';

export interface Series {
	/** Stable identity — the colour follows this, not the row order. */
	key: string;
	label: string;
	color: string;
	points: Point[];
}

const PAD = { top: 14, right: 18, bottom: 28, left: 52 };

// ------------------------------------------------------------------- helpers

/** Width of a block element, tracked through resizes (charts are fluid). */
function useContainerWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
	const ref = useRef<T>(null);
	const [width, setWidth] = useState(0);
	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		const measure = () => setWidth(el.clientWidth);
		measure();
		if (typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', measure);
			return () => window.removeEventListener('resize', measure);
		}
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);
	return [ref, width];
}

/** Round the axis top to a clean 1/2/2.5/5×10^k step so ticks read as numbers. */
function niceScale(max: number, tickCount = 4): { top: number; ticks: number[] } {
	if (!Number.isFinite(max) || max <= 0) return { top: 1, ticks: [0, 1] };
	const rough = max / tickCount;
	const mag = Math.pow(10, Math.floor(Math.log10(rough)));
	const norm = rough / mag;
	const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
	const top = Math.ceil(max / step) * step;
	const ticks: number[] = [];
	for (let v = 0; v <= top + step / 2; v += step) ticks.push(Math.round(v * 1000) / 1000);
	return { top, ticks };
}

/** Evenly spaced x labels that never collide, always including first and last. */
function labelIndices(count: number, width: number): Set<number> {
	if (count <= 1) return new Set([0]);
	const max = Math.max(2, Math.min(8, Math.floor(width / 90)));
	const stride = Math.max(1, Math.ceil((count - 1) / (max - 1)));
	const out = new Set<number>();
	for (let i = 0; i < count; i += stride) out.add(i);
	out.add(count - 1);
	// Drop the second-to-last tick if the stride left it hugging the end.
	const sorted = [...out].sort((a, b) => a - b);
	if (sorted.length > 2 && sorted[sorted.length - 1] - sorted[sorted.length - 2] < stride / 2) {
		out.delete(sorted[sorted.length - 2]);
	}
	return out;
}

export const isEmptySeries = (series: Series[]): boolean =>
	series.every((s) => s.points.every((p) => !p.value));

// --------------------------------------------------------------- tooltip bits

interface HoverState {
	index: number;
	x: number;
	y: number;
}

const Tooltip = ({
	hover,
	series,
	granularity,
	unit,
	width,
}: {
	hover: HoverState;
	series: Series[];
	granularity: Granularity;
	unit: Unit;
	width: number;
}) => {
	const day = series[0]?.points[hover.index]?.day;
	if (!day) return null;
	// Flip to the left of the pointer near the right edge so it never clips.
	const flip = hover.x > width - 190;
	return (
		<div
			className='pointer-events-none absolute z-20 min-w-[168px] rounded-xl border border-gray-100 bg-white/95 px-3 py-2 shadow-xl backdrop-blur-sm'
			style={{
				left: flip ? undefined : hover.x + 14,
				right: flip ? width - hover.x + 14 : undefined,
				top: Math.max(4, hover.y - 12),
			}}
		>
			<div className='mb-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400'>
				{formatBucketLong(day, granularity)}
			</div>
			{series.map((s) => (
				<div key={s.key} className='flex items-center justify-between gap-4 py-0.5'>
					<span className='flex items-center gap-2 text-xs text-gray-500'>
						<span
							className='inline-block h-0.5 w-3.5 rounded-full'
							style={{ backgroundColor: s.color }}
						/>
						{s.label}
					</span>
					<span className='text-sm font-bold tabular-nums text-gray-900'>
						{formatByUnit(s.points[hover.index]?.value ?? 0, unit)}
					</span>
				</div>
			))}
		</div>
	);
};

/** Shared hover/keyboard plumbing for the plot area. */
function usePlotHover(count: number) {
	const [hover, setHover] = useState<HoverState | null>(null);
	const move = useCallback(
		(clientX: number, clientY: number, rect: DOMRect, innerW: number) => {
			if (count === 0) return;
			const x = clientX - rect.left;
			const t = (x - PAD.left) / Math.max(1, innerW);
			const index = Math.max(0, Math.min(count - 1, Math.round(t * (count - 1))));
			setHover({ index, x, y: clientY - rect.top });
		},
		[count]
	);
	const step = useCallback(
		(delta: number, innerW: number) => {
			setHover((h) => {
				const index = Math.max(0, Math.min(count - 1, (h?.index ?? count - 1) + delta));
				const x = PAD.left + (count > 1 ? (index / (count - 1)) * innerW : innerW / 2);
				return { index, x, y: PAD.top + 8 };
			});
		},
		[count]
	);
	return { hover, setHover, move, step };
}

// ----------------------------------------------------------------- line chart

export const LineChart = ({
	series,
	granularity,
	unit = 'count',
	height = 236,
}: {
	series: Series[];
	granularity: Granularity;
	unit?: Unit;
	height?: number;
}) => {
	const [mounted, setMounted] = useState(false);
	React.useEffect(() => {
		const timer = setTimeout(() => setMounted(true), 50);
		return () => clearTimeout(timer);
	}, []);

	const [ref, width] = useContainerWidth<HTMLDivElement>();
	const count = series[0]?.points.length ?? 0;
	const { hover, setHover, move, step } = usePlotHover(count);
	const svgRef = useRef<SVGSVGElement>(null);

	const innerW = Math.max(10, width - PAD.left - PAD.right);
	const innerH = Math.max(10, height - PAD.top - PAD.bottom);
	const max = useMemo(
		() => Math.max(...series.flatMap((s) => s.points.map((p) => p.value)), 0),
		[series]
	);
	const { top, ticks } = useMemo(() => niceScale(max), [max]);

	const xAt = (i: number) => PAD.left + (count > 1 ? (i / (count - 1)) * innerW : innerW / 2);
	const yAt = (v: number) => PAD.top + innerH - (v / top) * innerH;
	const animatedY = (v: number) => (mounted ? yAt(v) : yAt(0));
	const labels = useMemo(() => labelIndices(count, innerW), [count, innerW]);

	return (
		<div className='relative' ref={ref}>
			{width > 0 && (
				<svg
					ref={svgRef}
					width={width}
					height={height}
					role='img'
					tabIndex={0}
					className='outline-none focus-visible:ring-2 focus-visible:ring-blue-200 rounded-lg'
					onPointerMove={(e) =>
						move(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect(), innerW)
					}
					onPointerLeave={() => setHover(null)}
					onFocus={() => step(0, innerW)}
					onBlur={() => setHover(null)}
					onKeyDown={(e) => {
						if (e.key === 'ArrowRight') {
							e.preventDefault();
							step(1, innerW);
						} else if (e.key === 'ArrowLeft') {
							e.preventDefault();
							step(-1, innerW);
						} else if (e.key === 'Escape') {
							setHover(null);
						}
					}}
				>
					{/* gridlines + y ticks */}
					{ticks.map((t) => (
						<g key={t}>
							<line
								x1={PAD.left}
								x2={PAD.left + innerW}
								y1={yAt(t)}
								y2={yAt(t)}
								stroke={t === 0 ? INK.axis : INK.grid}
								strokeWidth={1}
							/>
							<text
								x={PAD.left - 10}
								y={yAt(t) + 4}
								textAnchor='end'
								fontSize={11}
								fill={INK.muted}
								style={{ fontVariantNumeric: 'tabular-nums' }}
							>
								{unit === 'seconds' ? formatByUnit(t, unit) : formatCompact(t)}
							</text>
						</g>
					))}

					{/* x labels */}
					{series[0]?.points.map((p, i) =>
						labels.has(i) ? (
							<text
								key={p.day}
								x={xAt(i)}
								y={height - 8}
								textAnchor={i === 0 ? 'start' : i === count - 1 ? 'end' : 'middle'}
								fontSize={11}
								fill={INK.muted}
							>
								{formatBucket(p.day, granularity === 'month' ? 'month' : 'day')}
							</text>
						) : null
					)}

					{/* area wash — only for a single series, so fills never stack up */}
					{series.length === 1 && count > 1 && (
						<path
							d={`M${xAt(0)},${yAt(0)} ${series[0].points
								.map((p, i) => `L${xAt(i)},${animatedY(p.value)}`)
								.join(' ')} L${xAt(count - 1)},${yAt(0)} Z`}
							fill={series[0].color}
							fillOpacity={0.1}
							style={{ transition: 'd 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }}
						/>
					)}

					{/* crosshair under the lines so it never hides a mark */}
					{hover && (
						<line
							x1={xAt(hover.index)}
							x2={xAt(hover.index)}
							y1={PAD.top}
							y2={PAD.top + innerH}
							stroke={INK.axis}
							strokeWidth={1}
						/>
					)}

					{series.map((s) => (
						<g key={s.key}>
							{count === 1 ? (
								<circle
									cx={xAt(0)}
									cy={animatedY(s.points[0].value)}
									r={4}
									fill={s.color}
									style={{ transition: 'cy 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }}
								/>
							) : (
								<path
									d={s.points.map((p, i) => `${i ? 'L' : 'M'}${xAt(i)},${animatedY(p.value)}`).join(' ')}
									fill='none'
									stroke={s.color}
									strokeWidth={2}
									strokeLinejoin='round'
									strokeLinecap='round'
									style={{ transition: 'd 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }}
								/>
							)}
							{/* end dot with a 2px surface ring keeps it legible on a crossing */}
							{count > 1 && (
								<circle
									cx={xAt(count - 1)}
									cy={animatedY(s.points[count - 1].value)}
									r={4}
									fill={s.color}
									stroke={INK.surface}
									strokeWidth={2}
									style={{ transition: 'cy 0.8s cubic-bezier(0.16, 1, 0.3, 1)' }}
								/>
							)}
							{hover && (
								<circle
									cx={xAt(hover.index)}
									cy={yAt(s.points[hover.index]?.value ?? 0)}
									r={4.5}
									fill={s.color}
									stroke={INK.surface}
									strokeWidth={2}
								/>
							)}
						</g>
					))}
				</svg>
			)}
			{hover && (
				<Tooltip hover={hover} series={series} granularity={granularity} unit={unit} width={width} />
			)}
		</div>
	);
};

// ----------------------------------------------------------------- table view

export const SeriesTable = ({
	series,
	granularity,
	unit = 'count',
}: {
	series: Series[];
	granularity: Granularity;
	unit?: Unit;
}) => (
	<div className='max-h-[280px] overflow-auto rounded-xl border border-gray-100'>
		<table className='w-full text-left text-sm'>
			<thead className='sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500'>
				<tr>
					<th className='px-3 py-2 font-bold'>Day</th>
					{series.map((s) => (
						<th key={s.key} className='px-3 py-2 text-right font-bold'>
							<span className='inline-flex items-center gap-1.5'>
								<span
									className='inline-block h-0.5 w-3 rounded-full'
									style={{ backgroundColor: s.color }}
								/>
								{s.label}
							</span>
						</th>
					))}
				</tr>
			</thead>
			<tbody className='divide-y divide-gray-100'>
				{(series[0]?.points ?? []).map((p, i) => (
					<tr key={p.day} className='hover:bg-blue-50/40'>
						<td className='whitespace-nowrap px-3 py-1.5 text-gray-500'>
							{formatBucket(p.day, granularity)}
						</td>
						{series.map((s) => (
							<td key={s.key} className='px-3 py-1.5 text-right tabular-nums text-gray-900'>
								{formatByUnit(s.points[i]?.value ?? 0, unit)}
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

// ------------------------------------------------------------------ card shell

export const Legend = ({ series }: { series: Series[] }) => (
	<div className='flex flex-wrap items-center gap-x-4 gap-y-1.5'>
		{series.map((s) => (
			<span key={s.key} className='flex items-center gap-2 text-xs font-semibold text-gray-600'>
				<span
					className='inline-block h-0.5 w-4 rounded-full'
					style={{ backgroundColor: s.color }}
				/>
				{s.label}
			</span>
		))}
	</div>
);

/**
 * One metric in a card: the range headline, the plot, and a table toggle. The
 * toggle is not decoration — three palette slots sit below 3:1 contrast on
 * white, so the table is the guaranteed-readable twin of every chart.
 *
 * `notTracked` renders the greyed placeholder for a metric the backend cannot
 * answer yet — the card still occupies its slot, so the dashboard shows the
 * whole picture including the holes.
 *
 * `text-left` is deliberate: App.css centres everything under `.App`, which
 * would otherwise centre the title against its subtitle inside a left-aligned
 * block.
 */
export const ChartCard = ({
	title,
	subtitle,
	series,
	unit = 'count',
	granularity,
	height,
	emptyNote,
	loading,
	className = '',
	stat,
	notTracked,
	footer,
}: {
	title: string;
	subtitle?: string;
	series: Series[];
	unit?: Unit;
	granularity: Granularity;
	height?: number;
	emptyNote?: string;
	loading?: boolean;
	className?: string;
	/** Headline figure for the whole range, shown above the plot. */
	stat?: { value: string; caption: string };
	/** Reason this metric has no backend source; renders the greyed state. */
	notTracked?: string;
	footer?: React.ReactNode;
}) => {
	const [asTable, setAsTable] = useState(false);
	const empty = series.length === 0 || isEmptySeries(series);

	if (notTracked) {
		return (
			<div
				className={`rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-5 text-left ${className}`}
			>
				<div className='mb-3 flex items-start justify-between gap-3'>
					<div className='min-w-0'>
						<h3 className='m-0 truncate text-base font-bold text-gray-400'>{title}</h3>
						{subtitle && <p className='m-0 mt-0.5 text-xs text-gray-400'>{subtitle}</p>}
					</div>
					<span className='shrink-0 rounded-lg bg-gray-200 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-500'>
						Not tracked yet
					</span>
				</div>
				<div
					className='flex flex-col items-center justify-center rounded-xl border border-gray-100 bg-white/60 px-6 text-center'
					style={{ height: height ?? 236 }}
				>
					<p className='m-0 text-sm font-bold text-gray-400'>Nothing to chart</p>
					<p className='m-0 mt-1 max-w-sm text-xs leading-relaxed text-gray-400'>{notTracked}</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<style>{`
				@keyframes fadeSlideUp {
					from { opacity: 0; transform: translateY(16px); }
					to { opacity: 1; transform: translateY(0); }
				}
				.animate-card-enter {
					animation: fadeSlideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
				}
			`}</style>
			<div
				className={`animate-card-enter rounded-2xl border border-gray-100 bg-white p-5 shadow-sm text-left transition-opacity duration-200 ${loading ? 'opacity-60' : ''} ${className}`}
			>
				<div className='mb-3 flex items-start justify-between gap-3'>
				<div className='min-w-0'>
					<h3 className='m-0 truncate text-base font-bold text-gray-900'>{title}</h3>
					{subtitle && <p className='m-0 mt-0.5 text-xs text-gray-500'>{subtitle}</p>}
				</div>
				<button
					type='button'
					onClick={() => setAsTable((v) => !v)}
					title={asTable ? 'Show chart' : 'Show values as a table'}
					aria-label={asTable ? 'Show chart' : 'Show values as a table'}
					className='shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700'
				>
					{asTable ? <LineChartIcon className='h-4 w-4' /> : <Table2 className='h-4 w-4' />}
				</button>
			</div>

			{stat && (
				<div className='mb-3'>
					<div className='text-3xl font-extrabold leading-none text-gray-900'>{stat.value}</div>
					<div className='mt-1 text-xs font-semibold text-gray-500'>{stat.caption}</div>
				</div>
			)}

			{series.length > 1 && (
				<div className='mb-3'>
					<Legend series={series} />
				</div>
			)}

			{empty ? (
				<div
					className='flex flex-col items-center justify-center rounded-xl bg-gray-50/70 text-center'
					style={{ height: height ?? 236 }}
				>
					<p className='m-0 text-sm font-bold text-gray-500'>No data yet</p>
					{emptyNote && <p className='m-0 mt-1 max-w-xs text-xs text-gray-400'>{emptyNote}</p>}
				</div>
			) : asTable ? (
				<SeriesTable series={series} granularity={granularity} unit={unit} />
			) : (
				<LineChart
					key={series[0]?.points.length ?? 0}
					series={series}
					granularity={granularity}
					unit={unit}
					height={height}
				/>
			)}

			{footer && <div className='mt-3 border-t border-gray-100 pt-3 text-xs text-gray-500'>{footer}</div>}
		</div>
		</>
	);
};

